import * as React from 'react';
import { getIntrinsicElementProps, mergeCallbacks, slot } from '@fluentui/react-components';
import type { GhostTextProps, GhostTextState } from './GhostText.types';
import { $getNodeByKey, useLexicalComposerContext } from '@fluentui-copilot/react-text-editor';

// If you add JSX to this file, be sure to change the file type to .tsx

/**
 * Create the state required to render GhostText.
 *
 * The returned state can be modified with hooks such as useGhostTextStyles_unstable,
 * before being passed to renderGhostText_unstable.
 *
 * @param props - props from this instance of GhostText
 * @param ref - reference to root HTMLElement of GhostText
 */
export const useGhostText_unstable = (props: GhostTextProps, ref: React.Ref<HTMLSpanElement>): GhostTextState => {
  const { nodeKey, onClick, ...rest } = props;
  const [editor] = useLexicalComposerContext();

  const handleClick = React.useCallback(
    (_ev: React.MouseEvent<HTMLElement>) => {
      if (!nodeKey) {
        return;
      }

      editor.update(
        () => {
          const node = $getNodeByKey(nodeKey);
          node?.getPreviousSibling()?.selectEnd();
        },
        { tag: 'historic' },
      );
    },
    [editor, nodeKey],
  );

  const mergedOnClick = mergeCallbacks(handleClick, onClick);

  const state: GhostTextState = {
    components: {
      root: 'span',
      indicator: 'span',
    },
    root: slot.always(
      getIntrinsicElementProps('span', {
        ref,
        onClick: mergedOnClick,
        ...rest,
      }),
      { elementType: 'span' },
    ),
    indicator: slot.optional(props.indicator, { elementType: 'span' }),
  };

  return state;
};
